import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { HelpCircle, Mail, BookOpen, ChevronDown, ChevronUp, Zap, AlertTriangle, Clock, Users, Settings } from 'lucide-react';
import SupportChat from '../components/support/SupportChat';

const faqs = [
  {
    question: 'What does TutorTracer monitor?',
    answer: 'TutorTracer monitors recorded tutoring sessions from platforms like Lessonspace and Zoom. It analyses transcripts using AI to assess teaching quality across five dimensions (clarity, engagement, adaptability, encouragement, structure), detect potential issues (safeguarding concerns, poaching attempts, teaching errors, organisational criticism), and track punctuality.',
  },
  {
    question: 'How are quality scores calculated?',
    answer: 'Each session is assessed across five teaching dimensions: Clarity, Engagement, Adaptability, Encouragement, and Structure. Each dimension receives a rating of Strong, Adequate, or Needs Development. The overall quality score is the average of these ratings, converted to a percentage. A tutor\'s aggregate score reflects their performance across all reviewed sessions.',
  },
  {
    question: 'What do the issue categories mean?',
    answer: 'Issues are grouped into four categories: Inappropriate Discussion (safeguarding concerns or inappropriate content), Poaching Risk (attempts to arrange sessions outside the platform), Disparagement (negative comments about the organisation), and Teaching Error (factual mistakes in lesson content). Each issue includes the flagged quote, context, and a link to the relevant point in the recording.',
  },
  {
    question: 'How does lateness detection work?',
    answer: 'TutorTracer compares participant join times against an inferred scheduled time (the nearest half hour). Lateness is only flagged when the first participant joins more than 10 minutes after that time, to reduce false positives. Severity thresholds (Minor, Moderate, Severe) are configurable in Settings > Analysis.',
  },
  {
    question: 'What is the Intelligence page?',
    answer: 'The Intelligence page uses AI to analyse patterns across all your sessions and generate prioritised recommendations. These might include tutors who need coaching, recurring quality issues, or positive trends to celebrate. Recommendations are refreshed as new sessions are processed.',
  },
  {
    question: 'How do I connect my Lessonspace or Zoom account?',
    answer: 'Go to Settings > Integrations. For Lessonspace, enter your API key and enable continuous sync. For Zoom, enter your Account ID, Client ID, and Client Secret (requires Zoom Pro with Cloud Recording). You can trigger manual syncs or enable automatic 15-minute polling.',
  },
  {
    question: 'Can I customise which issues are flagged?',
    answer: 'Yes. In Settings > Analysis, you can adjust Indicator Importance Weights on a 1-5 scale. Higher weights mean the AI will be more sensitive to that type of issue. You can also adjust lateness thresholds there, and tune star performer ranking in Settings > Scoring.',
  },
  {
    question: 'How do I add team members?',
    answer: 'Go to Settings > Team and click "Add Member". Enter their email and assign a role (Admin, Manager, Reviewer, or Read-only). They\'ll receive an invitation email. Admins can manage all settings, while Reviewers can only view and review flagged issues.',
  },
];

const gettingStartedSteps = [
  { icon: Settings, title: 'Connect your platform', description: 'Go to Settings > Integrations and add your Lessonspace API key or Zoom credentials.' },
  { icon: Zap, title: 'Sync your sessions', description: 'Trigger a manual sync or enable continuous sync. Sessions will be automatically transcribed and analysed.' },
  { icon: BookOpen, title: 'Review your first lesson', description: 'Once processing completes, visit Lessons to see quality assessments, transcripts, and session summaries.' },
  { icon: AlertTriangle, title: 'Check flagged issues', description: 'The Flagged Issues page shows any concerns detected by AI. Review, dismiss, or escalate each one.' },
  { icon: Users, title: 'Explore tutor profiles', description: 'Visit Tutors to see performance radars, strengths, and focus areas for each tutor on your team.' },
];

const Support = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [expandedFaq, setExpandedFaq] = useState(null);
  const cardClass = `rounded-lg border p-5 ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`;

  return (
    <div className="space-y-8 ">
      <h1 className={`text-2xl font-semibold font-[Manrope] tracking-tight ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
        Help & Support
      </h1>

      {/* Getting Started */}
      <div>
        <h2 className={`text-sm font-medium uppercase tracking-wider mb-4 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
          Getting Started
        </h2>
        <div className="space-y-3">
          {gettingStartedSteps.map((step, idx) => (
            <div key={idx} className={`${cardClass} flex items-start gap-4`}>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-[#18181b]' : 'bg-[#f5f5f5]'}`}>
                <step.icon className={`h-4 w-4 ${isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}`} strokeWidth={1.5} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${isDark ? 'bg-[#27272a] text-[#a1a1aa]' : 'bg-[#e8e8e8] text-[#666]'}`}>
                    {idx + 1}
                  </span>
                  <h3 className={`text-sm font-medium ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{step.title}</h3>
                </div>
                <p className={`text-sm mt-1 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* FAQs */}
      <div>
        <h2 className={`text-sm font-medium uppercase tracking-wider mb-4 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
          Frequently Asked Questions
        </h2>
        <div className={`rounded-lg border overflow-hidden ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
          {faqs.map((faq, idx) => {
            const isExpanded = expandedFaq === idx;
            return (
              <div key={idx} className={idx > 0 ? `border-t ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}` : ''}>
                <button
                  onClick={() => setExpandedFaq(isExpanded ? null : idx)}
                  className={`w-full flex items-center justify-between px-5 py-4 text-left transition-colors ${isDark ? 'hover:bg-[#18181b]/50' : 'hover:bg-[#fafafa]'}`}
                >
                  <span className={`text-sm font-medium pr-4 ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{faq.question}</span>
                  {isExpanded
                    ? <ChevronUp className={`h-4 w-4 flex-shrink-0 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`} />
                    : <ChevronDown className={`h-4 w-4 flex-shrink-0 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`} />
                  }
                </button>
                {isExpanded && (
                  <div className={`px-5 pb-4 ${isDark ? 'bg-[#0d0d0f]' : 'bg-[#fafafa]'}`}>
                    <p className={`text-sm leading-relaxed ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{faq.answer}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Contact */}
      <div className={cardClass}>
        <div className="flex items-center gap-2 mb-3">
          <Mail className={`h-4 w-4 ${isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}`} strokeWidth={1.5} />
          <h3 className={`text-sm font-medium ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>Need more help?</h3>
        </div>
        <p className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
          Reach out to us at{' '}
          <a
            href="mailto:support@tutortracer.com?subject=Support%20Request"
            className={`underline ${isDark ? 'text-[#7662ac] hover:text-white' : 'text-[#586f8d] hover:text-[#1a1a1a]'}`}
          >
            support@tutortracer.com
          </a>
          {' '}and we'll get back to you within 24 hours.
        </p>
      </div>

      <SupportChat />
    </div>
  );
};

export default Support;
